import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDoctorStore } from '@/stores/doctorStore'
import { useHospitalStore } from '@/stores/hospitalStore'

const specialties = [
  '消化器内科',
  '内科',
  '総合内科',
  '小児科',
  '精神科',
  '皮膚科',
  '外科',
  '研究',
  'その他',
]

const PERMISSIONS = [
  '患者閲覧',
  '患者編集',
  '診療記録閲覧',
  '診療記録編集',
  '予約管理',
  'レポート閲覧',
]

export default function DoctorEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'create' || !id
  
  const { getDoctor, addDoctor, updateDoctor } = useDoctorStore()
  const { hospitals } = useHospitalStore()
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    hospitalId: '',
    specialty: '',
    licenseNumber: '',
    tel: '',
    status: 'active' as 'active' | 'inactive',
    permissions: ['患者閲覧', '診療記録閲覧'] as string[],
  })

  useEffect(() => {
    if (!isNew && id) {
      const doctor = getDoctor(id)
      if (doctor) {
        setFormData({
          name: doctor.name,
          email: doctor.email,
          hospitalId: doctor.hospitalId,
          specialty: doctor.specialty,
          licenseNumber: doctor.licenseNumber,
          tel: doctor.tel,
          status: doctor.status,
          permissions: doctor.permissions || [],
        })
      }
    }
  }, [id, isNew, getDoctor])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const hospital = hospitals.find(h => h.id === formData.hospitalId)
    
    if (isNew) {
      addDoctor({
        name: formData.name,
        email: formData.email,
        hospitalId: formData.hospitalId,
        hospitalName: hospital?.name || '',
        specialty: formData.specialty,
        licenseNumber: formData.licenseNumber,
        tel: formData.tel,
        status: formData.status,
        permissions: formData.permissions,
      })
    } else if (id) {
      updateDoctor(id, {
        name: formData.name,
        email: formData.email,
        hospitalId: formData.hospitalId,
        hospitalName: hospital?.name || '',
        specialty: formData.specialty,
        licenseNumber: formData.licenseNumber,
        tel: formData.tel,
        status: formData.status,
        permissions: formData.permissions,
      })
    }
    
    navigate('/master/doctors')
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to="/master/doctors" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">
            医師 {isNew ? '新規登録' : '編集'}
          </h1>
        </div>
      </header>

      <div className="aip-main__content">
        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-header">
            <h2 className="form-title">{isNew ? '新規医師を登録' : '医師情報を編集'}</h2>
            <p className="form-description">
              医師の基本情報を入力してください
            </p>
          </div>

          <div className="form-body">
            {/* 基本情報 */}
            <section className="form-section">
              <h3 className="form-section__title">基本情報</h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="name">
                    氏名<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="例：吉岡 紘平"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="licenseNumber">
                    医師免許番号
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="licenseNumber"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    placeholder="例：MD-123456"
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="hospitalId">
                    所属病院<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-select"
                    id="hospitalId"
                    name="hospitalId"
                    value={formData.hospitalId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    {hospitals.map((hospital) => (
                      <option key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="specialty">
                    専門分野<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-select"
                    id="specialty"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    {specialties.map((specialty) => (
                      <option key={specialty} value={specialty}>{specialty}</option>
                    ))}
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label">
                    状態
                  </label>
                  <select
                    className="aip-select"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                  </select>
                </div>
              </div>
            </section>

            {/* 連絡先 */}
            <section className="form-section">
              <h3 className="form-section__title">連絡先</h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="email">
                    メールアドレス<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="例：doctor@example.jp"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="tel">
                    電話番号
                  </label>
                  <input
                    className="aip-input-text"
                    type="tel"
                    id="tel"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    placeholder="例：03-1234-5678"
                  />
                </div>
              </div>
            </section>

            {/* 権限 */}
            <section className="form-section">
              <h3 className="form-section__title">権限設定</h3>
              <div className="permission-group">
                {PERMISSIONS.map((permission) => (
                  <label key={permission} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission)}
                      onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                    />
                    <span>{permission}</span>
                  </label>
                ))}
              </div>
            </section>
          </div>

          {/* フッター */}
          <div className="form-footer">
            <Link to="/master/doctors" className="aip-button aip-button--outline">
              キャンセル
            </Link>
            <button type="submit" className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">save</span>
              {isNew ? '登録' : '保存'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .form-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .form-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .form-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .form-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .form-body {
          padding: var(--spacing-lg);
        }

        .form-section {
          margin-bottom: var(--spacing-xl);
        }

        .form-section__title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .aip-floating-field {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .aip-floating-field__label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .aip-input-text,
        .aip-select,
        .aip-textarea {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .aip-input-text:focus,
        .aip-select:focus,
        .aip-textarea:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .aip-textarea {
          resize: vertical;
        }

        .required {
          color: var(--color-alert);
          font-size: 0.75rem;
          margin-left: var(--spacing-xs);
        }

        .permission-group {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: var(--spacing-sm);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          cursor: pointer;
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-solid-gray25);
          border-radius: var(--border-radius-md);
          transition: background-color 0.2s;
        }

        .checkbox-label:hover {
          background-color: var(--color-blue50);
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: var(--color-blue500);
        }

        .form-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .permission-group {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}
